import _ from 'underscore';
import Promise from 'bluebird';
import stringify from 'json-stable-stringify';
import { Subject } from '@bingads-webui-universal/observer-pattern';
import { ResourceCacheIndex } from './cache-index/index';

export class BaseCacheManager extends Subject {
  /**
   * An abstraction of ClientCacheManager and ServerCacheManager
   */
  constructor() {
    super();

    /**
     * Resource based index of cache entries
     * @type {ResourceCacheIndex}
     */
    this.cacheIndex = new ResourceCacheIndex(this);
  }

  /**
   * Get the storage cache for the given cache key
   * @abstract
   * @param {Object} key - The cache key
   * @returns {TimeAwareCache} - The storage cache for the given key
   */
  storageCacheFor(/* key */) {
    return null;
  }

  /**
   * Serialize the cache key
   * @param {Object} key - The cache key
   * @returns {string} - The string representation of the cache key
   */
  serializeKey(key) {
    return stringify(key);
  }

  /**
   * Deserialize the cache key from a string
   * @param {Object} keyStr - The string representation of the cache key
   * @returns {string} - The deserialized cache key
   */
  deserializeKey(keyStr) {
    return JSON.parse(keyStr);
  }

  /**
   * Invalidate cache items for a given resource (entity type)
   * @param {string} resource - Name of the resource
   * @returns {void}
   */
  invalidate(resource) {
    _.each(this.cacheIndex.keys({ resource }), (keyStr) => {
      this.del(this.deserializeKey(keyStr));
    });
  }

  /**
   * Get cached value for a key
   * @param {Object} key - The cache key
   * @returns {*} - The cached value
   */
  get(key) {
    const cache = this.storageCacheFor(key);

    return cache ? cache.get(this.serializeKey(key)) : null;
  }

  /**
   * Set a value for a key
   * @param {Object} key - The cache key
   * @param {*} value - The cache value
   * @param {number} ttl - The life cycle of the cache entry (in milliseconds)
   * @returns {*} - The cached value. In case the value is a Promise, it would
   *    return a new promise resolves to the same value after the cache item is
   *    fully settled.
   */
  set(key, value, ttl) {
    const cache = this.storageCacheFor(key);

    if (cache) {
      const keyStr = this.serializeKey(key);

      cache.set(keyStr, value, ttl);

      if (Promise.resolve(value) === value) {
        return value.catch((err) => {
          cache.del(keyStr);
          throw err;
        });
      }
    }
    return value;
  }

  /**
   * Delete a cache entry
   * @param {Object} key - The cache key
   * @returns {void}
   */
  del(key) {
    const cache = this.storageCacheFor(key);

    if (cache) {
      cache.del(this.serializeKey(key));
    }
  }

  /**
   * Delegate the onGet notification
   * @private
   * @param {TimeAwareCache} cache - The issued storage cache
   * @param {number} timestamp - Timestamp of the action
   * @param {CacheEntry} entry - The matched cache entry
   * @returns {void}
   */
  onGet(cache, timestamp, entry) {
    this.notify('onGet', timestamp, entry);
  }

  /**
   * Delegate the onSet notification
   * @private
   * @param {TimeAwareCache} cache - The issued storage cache
   * @param {number} timestamp - Timestamp of the action
   * @param {CacheEntry} entry - The written cache entry
   * @param {string} keyToReplace - The key of replaced cache item
   * @returns {void}
   */
  onSet(cache, timestamp, entry, keyToReplace) {
    this.notify('onSet', timestamp, entry, keyToReplace);
  }

  /**
   * Delegate the onDel notification
   * @private
   * @param {TimeAwareCache} cache - The issued storage cache
   * @param {number} timestamp - Timestamp of the action
   * @param {CacheEntry} entry - The cache entry to be deleted
   * @returns {void}
   */
  onDel(cache, timestamp, entry) {
    this.notify('onDel', timestamp, entry);
  }
}
